import {
  getWorkspacePublicDocViewsQuery,
  type GetWorkspacePublicDocViewsQuery,
  getWorkspacePublicPagesDetailQuery,
  type GetWorkspacePublicPagesDetailQuery,
  publishDocViewMutation,
  revokePublicDocViewMutation,
  revokePublicPageMutation,
} from '@affine/graphql';
import { LiveData, Service } from '@toeverything/infra';

import type { WorkspaceServerService } from '../../cloud';
import type { WorkspaceService } from '../../workspace';

export type PublicDocLink =
  GetWorkspacePublicPagesDetailQuery['workspace']['publicDocs'][number];
export type PublicDocViewLink =
  GetWorkspacePublicDocViewsQuery['workspace']['publicDocViews'][number];

/**
 * All public links of the workspace: published docs and public table views.
 * Backs the "Public links" settings page and the view tab context menu.
 */
export class PublicLinksService extends Service {
  readonly docs$ = new LiveData<PublicDocLink[]>([]);
  readonly views$ = new LiveData<PublicDocViewLink[]>([]);
  readonly isLoading$ = new LiveData(false);
  readonly error$ = new LiveData<unknown>(null);

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService
  ) {
    super();
  }

  private get workspaceId() {
    return this.workspaceService.workspace.id;
  }

  private get server() {
    const server = this.workspaceServerService.server;
    if (!server) {
      throw new Error('No Server');
    }
    return server;
  }

  /** The public link of a view, from the last loaded list. */
  findViewLink(docId: string, blockId: string, viewId: string) {
    return this.views$.value.find(
      v => v.docId === docId && v.blockId === blockId && v.viewId === viewId
    );
  }

  viewLinkUrl(id: string) {
    return new URL(`/public-view/${id}`, this.server.baseUrl).toString();
  }

  private lastLoadedAt = 0;
  private loading: Promise<void> | null = null;

  /** Refresh the list unless it was loaded in the last 30 seconds. */
  revalidateIfStale() {
    if (this.loading || Date.now() - this.lastLoadedAt < 30_000) {
      return this.loading ?? Promise.resolve();
    }
    return this.revalidate();
  }

  revalidate() {
    this.loading ??= this.load().finally(() => {
      this.loading = null;
    });
    return this.loading;
  }

  private async load() {
    if (!this.workspaceServerService.server) return;
    this.isLoading$.next(true);
    try {
      const [docs, views] = await Promise.all([
        this.server.gql({
          query: getWorkspacePublicPagesDetailQuery,
          variables: { workspaceId: this.workspaceId },
        }),
        this.server.gql({
          query: getWorkspacePublicDocViewsQuery,
          variables: { workspaceId: this.workspaceId },
        }),
      ]);
      this.docs$.next(docs.workspace.publicDocs);
      this.views$.next(views.workspace.publicDocViews);
      this.error$.next(null);
      this.lastLoadedAt = Date.now();
    } catch (error) {
      this.error$.next(error);
    } finally {
      this.isLoading$.next(false);
    }
  }

  async publishView(docId: string, blockId: string, viewId: string) {
    const data = await this.server.gql({
      query: publishDocViewMutation,
      variables: { workspaceId: this.workspaceId, docId, blockId, viewId },
    });
    const link = data.publishDocView;
    this.views$.next([
      link,
      ...this.views$.value.filter(v => v.id !== link.id),
    ]);
    return link;
  }

  async revokeView(id: string) {
    await this.server.gql({
      query: revokePublicDocViewMutation,
      variables: { workspaceId: this.workspaceId, id },
    });
    this.views$.next(this.views$.value.filter(v => v.id !== id));
  }

  async revokeDoc(docId: string) {
    await this.server.gql({
      query: revokePublicPageMutation,
      variables: { workspaceId: this.workspaceId, pageId: docId },
    });
    this.docs$.next(this.docs$.value.filter(d => d.id !== docId));
  }
}
